import { UserAvatar } from "@/components/ui/stacked-avatars";
import {
  useConnectActivity,
  type ConnectActivityConfig,
  type ConnectActivityLevelResult,
} from "@/lib/api/activities";
import { ShareableEntity } from "@/lib/api/shareable-entities";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  dimUnplayedBars,
  formatAudioDuration,
  useAudioBlob,
  useStaticAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import { colorsV2 } from "@somethings-ui/core";
import { cn } from "@/lib/utils";
import { getQuestionById } from "@somethings-ui/core/connect-questions";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { useMemo } from "react";
import { ConnectLevelThumbnail } from "@/components/conversations/activities/connect-moment/connect-level-thumbnail";
import { ConnectSummaryThumbnail } from "@/components/conversations/activities/connect-moment/connect-summary-thumbnail";
import { PeriwinkleCard } from "@/components/conversations/activities/connect-moment/periwinkle-card";
import { EntityHeader } from "../entity-header";
import { EntityComments } from "../interactions/entity-comments";
import { EntityReactions } from "../interactions/entity-reactions";
import { InteractionFooter } from "../interactions/interaction-footer";
import { useEntityInteractionHandlers } from "../interactions/use-entity-interaction-handlers";
import {
  filterInteractionsByCardIndex,
  transformInteractionsToComments,
  transformInteractionsToReactions,
} from "../interactions/utils";
import { ConnectSummaryCard } from "./connect-summary-card";
import { useHubStore } from "../use-hub-store";

type CarouselCard =
  | { kind: "summary" }
  | { kind: "level"; level: 0 | 1 | 2 };

const CAROUSEL_CARDS: CarouselCard[] = [
  { kind: "summary" },
  { kind: "level", level: 0 },
  { kind: "level", level: 1 },
  { kind: "level", level: 2 },
];

const clampIndex = (raw: number | null | undefined): number => {
  if (
    raw == null ||
    !Number.isInteger(raw) ||
    raw < 0 ||
    raw >= CAROUSEL_CARDS.length
  ) {
    return 0;
  }
  return raw;
};

interface ViewConnectMomentViewProps {
  entity: ShareableEntity;
  menteeId: string;
  mentorId: string;
  mentorAvatarUrl?: string | null;
  teenAvatarUrl?: string | null;
  currentIndex?: number;
  totalEntities?: number;
  isLoadingNextPage?: boolean;
  activeTab?: "new" | "all";
  initialIndex?: number | null;
  onPrevious?: () => void;
  onNext?: () => void;
  onNavigateToList?: () => void;
}

export function ViewConnectMomentView({
  entity,
  menteeId,
  mentorId,
  mentorAvatarUrl = null,
  teenAvatarUrl: fallbackTeenAvatarUrl = null,
  currentIndex = 1,
  totalEntities = 1,
  isLoadingNextPage = false,
  activeTab,
  onPrevious,
  onNext,
  onNavigateToList,
}: ViewConnectMomentViewProps) {
  const selectedIndex = useHubStore((state) => state.selectedIndex);
  const setSelectedCarouselIndex = useHubStore(
    (state) => state.setSelectedCarouselIndex,
  );
  const activeIndex = clampIndex(selectedIndex);
  const handlePrev = () =>
    setSelectedCarouselIndex(Math.max(0, activeIndex - 1));
  const handleNext = () =>
    setSelectedCarouselIndex(
      Math.min(CAROUSEL_CARDS.length - 1, activeIndex + 1),
    );

  const {
    user,
    isRead,
    isFromCurrentUser: _isFromCurrentUser,
    interactions,
    isInteractionPending,
    handleReaction,
    handleComment,
    toggleReadStatus,
  } = useEntityInteractionHandlers({
    entity,
    menteeId,
    entityType: "connect_moment",
    activeTab,
    onNavigateToList,
  });

  const cardInteractions = useMemo(
    () => filterInteractionsByCardIndex(interactions, activeIndex),
    [interactions, activeIndex],
  );
  const comments = useMemo(
    () => transformInteractionsToComments(cardInteractions, user?.id),
    [cardInteractions, user?.id],
  );
  const reactions = useMemo(
    () => transformInteractionsToReactions(cardInteractions),
    [cardInteractions],
  );

  const userIsMentor = user?.id === mentorId;

  const { data: activity } = useConnectActivity(entity.id);
  const config = activity?.config;
  const result = activity?.result;

  const teenName = entity.shares[0]?.sharedWith.name ?? "Teen";
  const teenAvatarUrl =
    entity.shares[0]?.sharedWith.profile_picture_url ??
    fallbackTeenAvatarUrl ??
    undefined;
  const ownerAvatarUrl =
    entity.owner.profile_picture_url ?? mentorAvatarUrl ?? undefined;
  const ownerName = entity.owner.name;

  const activeCard = CAROUSEL_CARDS[activeIndex];
  const activeLevel = activeCard.kind === "level" ? activeCard.level : null;
  const activeMentorAnswer =
    activeLevel != null ? config?.mentor_answers[activeLevel] : undefined;
  const activeQuestion = activeMentorAnswer?.question_id
    ? getQuestionById(activeMentorAnswer.question_id)
    : null;

  return (
    <div className="flex h-full flex-col bg-white">
      <EntityHeader
        title="Connection card"
        pagination={{
          current: currentIndex,
          total: totalEntities,
          isLoadingNextPage,
          onPrevious,
          onNext,
        }}
        owner={{
          name: ownerName,
          avatarUrl: ownerAvatarUrl,
        }}
        createdAt={entity.created_at}
      />
      <div className="flex-1 overflow-y-auto pt-2">

        <div className="relative px-4 pt-2">
          <div className="overflow-hidden">
            <div
              className="flex gap-3 transition-transform duration-300 ease-out"
              style={{
                transform: `translateX(calc(8% - ${activeIndex * 84}% - ${activeIndex * 12}px))`,
              }}
            >
              {CAROUSEL_CARDS.map((card, pos) =>
                card.kind === "summary" ? (
                  <ConnectSummaryCard
                    key="summary"
                    isActive={pos === activeIndex}
                    ownerName={ownerName}
                    ownerAvatarUrl={ownerAvatarUrl}
                    teenName={teenName}
                    teenAvatarUrl={teenAvatarUrl}
                  />
                ) : (
                  <ConnectLevelCard
                    key={`level-${card.level}`}
                    levelIndex={card.level}
                    isActive={pos === activeIndex}
                    mentorAnswer={config?.mentor_answers[card.level]}
                    levelResult={result?.levels[card.level] ?? null}
                    teenName={teenName}
                    teenAvatarUrl={teenAvatarUrl}
                    ownerName={ownerName}
                    ownerAvatarUrl={ownerAvatarUrl}
                  />
                ),
              )}
            </div>
          </div>

          {activeIndex > 0 && (
            <button
              type="button"
              onClick={handlePrev}
              aria-label="Previous card"
              className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {activeIndex < CAROUSEL_CARDS.length - 1 && (
            <button
              type="button"
              onClick={handleNext}
              aria-label="Next card"
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {reactions.length > 0 && (
          <EntityReactions reactions={reactions} className="mt-4 px-4" />
        )}

        {comments.length > 0 && (
          <div className="mt-4 px-4">
            <EntityComments comments={comments} />
          </div>
        )}
      </div>

      {userIsMentor && (
        <InteractionFooter
          ownerName={entity.owner.name}
          isRead={isRead}
          isPending={isInteractionPending}
          previewThumbnail={
            activeLevel != null ? (
              <ConnectLevelThumbnail
                levelIndex={activeLevel}
                questionText={activeQuestion?.text}
                size="md"
              />
            ) : (
              <ConnectSummaryThumbnail
                ownerName={ownerName}
                ownerAvatarUrl={ownerAvatarUrl}
                teenName={teenName}
                teenAvatarUrl={teenAvatarUrl}
                size="md"
              />
            )
          }
          onReaction={(emoji) => handleReaction(emoji, { index: activeIndex })}
          onComment={(text) => handleComment(text, { index: activeIndex })}
          onMarkAsRead={toggleReadStatus}
        />
      )}
    </div>
  );
}

interface ConnectLevelCardProps {
  levelIndex: number;
  isActive: boolean;
  mentorAnswer: ConnectActivityConfig["mentor_answers"][number] | undefined;
  levelResult: ConnectActivityLevelResult | null;
  teenName: string;
  teenAvatarUrl: string | undefined;
  ownerName: string;
  ownerAvatarUrl: string | undefined;
}

function ConnectLevelCard({
  levelIndex,
  isActive,
  mentorAnswer,
  levelResult,
  teenName,
  teenAvatarUrl,
  ownerName,
  ownerAvatarUrl,
}: ConnectLevelCardProps) {
  const tr = levelResult?.teen_response;
  const { url: mentorAudioUrl } = useSignedMedia(
    mentorAnswer?.audio_s3_key ?? undefined,
  );
  const { url: teenAudioUrl } = useSignedMedia(
    tr?.mode === "voice" ? tr.audio_s3_key : undefined,
  );

  if (!mentorAnswer) return null;

  const question = mentorAnswer.question_id
    ? getQuestionById(mentorAnswer.question_id)
    : null;

  return (
    <PeriwinkleCard
      variant="highlight"
      className={cn(
        "shrink-0 rounded-3xl transition-opacity",
        isActive ? "" : "opacity-70",
      )}
      style={{
        width: "84%",
        minHeight: 640,
        boxShadow: "0px 0px 16px 0px rgba(0,0,0,0.08)",
      }}
    >
      <div className="flex h-full flex-col gap-8 px-4 pb-6 pt-9">
        {/* Fixed-height header so audio rows start at the same Y across levels. */}
        <div className="flex h-[128px] flex-col items-center gap-2">
          <span className="text-base font-normal text-white">
            Level {levelIndex + 1}
          </span>
          <h2 className="line-clamp-3 text-center text-[28px] font-bold leading-[1.05] tracking-[-0.5px] text-white">
            {question?.text ?? "Question"}
          </h2>
        </div>

        <div className="flex flex-col gap-10">
          {mentorAudioUrl && (
            <DetailAudioBubble
              speaker={ownerName}
              avatarUrl={ownerAvatarUrl}
              audioUrl={mentorAudioUrl}
            />
          )}
          {tr?.mode === "voice" && teenAudioUrl ? (
            <DetailAudioBubble
              speaker={teenName}
              avatarUrl={teenAvatarUrl}
              audioUrl={teenAudioUrl}
            />
          ) : tr?.mode === "text" ? (
            <div className="flex flex-col gap-2">
              <UserAvatar
                imageUrl={teenAvatarUrl}
                name={teenName}
                size={50}
                showBorder
              />
              <div className="whitespace-pre-wrap break-words rounded-2xl bg-white/15 p-3 text-sm text-white">
                {tr.content}
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-white/10 px-3 py-2 text-xs text-white/75">
              Waiting on {teenName} to record their answer.
            </div>
          )}
        </div>
      </div>
    </PeriwinkleCard>
  );
}

interface DetailAudioBubbleProps {
  speaker: string;
  avatarUrl: string | undefined;
  audioUrl: string;
}

function DetailAudioBubble({
  speaker,
  avatarUrl,
  audioUrl,
}: DetailAudioBubbleProps) {
  const blob = useAudioBlob(audioUrl);
  const { isPlaying, progressMs, durationMs, toggle } =
    useAudioPlayback(audioUrl);

  const bars = useStaticAudioWaveform(blob);
  const playedBars = dimUnplayedBars(bars, durationMs, progressMs);

  return (
    <div className="flex flex-col gap-4">
      <div className="relative ml-1 h-[50px] w-[50px]">
        <div className="relative">
          <UserAvatar
            imageUrl={avatarUrl}
            name={speaker}
            size={50}
            showBorder
          />
        </div>
      </div>
      <div className="flex items-center gap-3 px-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? `Pause ${speaker}` : `Play ${speaker}`}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: colorsV2.brand.tangelo }}
        >
          {isPlaying ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="ml-0.5 h-3.5 w-3.5" />
          )}
        </button>
        {playedBars ? (
          <div
            className="flex flex-1 items-center gap-[1.5px]"
            style={{ height: 33 }}
          >
            {playedBars.map((v, i) => (
              <div
                key={i}
                style={{
                  width: 3,
                  height: `${Math.max(6, v * 33)}px`,
                  background: colorsV2.brand.tangelo,
                  borderRadius: 110,
                }}
              />
            ))}
          </div>
        ) : (
          <div className="h-8 flex-1 animate-pulse rounded bg-white/15" />
        )}
        <span className="text-sm font-normal text-white">
          {durationMs > 0 ? formatAudioDuration(durationMs) : "—"}
        </span>
      </div>
    </div>
  );
}
